/**
 * Classify person-hub curiosities into about prose vs labeled metadata.
 */

import { isUsefulCopy, usefulCuriosities } from './entityHubRenderable.js';

const LABEL_VALUE = /^([^:\n]{2,32}):\s*(.+)$/s;
const PLATFORM_ABOUT_SOURCES = new Set(['youtube', 'twitch', 'kick']);
const PLATFORM_ABOUT_RANK = { kick: 0, twitch: 1, youtube: 2 };

function isAboutText(text) {
  if (text.length >= 120) return true;
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  return sentences.length >= 2 && text.length >= 60;
}

/**
 * @param {object} item - curiosity from hub API
 * @returns {{ type: 'about', body: string } | { type: 'detail', label: string, value: string } | null}
 */
export function classifyCuriosityItem(item) {
  const text = (item?.text || item?.summary || '').trim();
  if (!isUsefulCopy(text)) return null;

  const apiKind = (item?.kind || '').toLowerCase();
  const apiTitle = (item?.title || '').trim();
  const source = String(item?.source || '').toLowerCase();

  if (PLATFORM_ABOUT_SOURCES.has(source)) {
    return { type: 'about', body: text, source };
  }

  if (apiTitle && apiTitle.toLowerCase() !== 'curiosity') {
    if (isAboutText(text) && apiTitle.toLowerCase() === 'about') {
      return { type: 'about', body: text };
    }
    return { type: 'detail', label: apiTitle, value: text };
  }

  const labelMatch = text.match(LABEL_VALUE);
  if (labelMatch) {
    const label = labelMatch[1].trim();
    const value = labelMatch[2].trim();
    if (value.length <= 120 && !isAboutText(text)) {
      return { type: 'detail', label, value };
    }
  }

  if (isAboutText(text)) {
    return { type: 'about', body: text };
  }

  if (apiKind && apiKind !== 'curiosity') {
    const label = apiKind.charAt(0).toUpperCase() + apiKind.slice(1);
    return { type: 'detail', label, value: text };
  }

  return { type: 'detail', label: 'Info', value: text };
}

/**
 * @param {object} hub
 * @param {{ preferredPlatform?: string, hasCreatorChannel?: boolean }} [options]
 */
export function partitionPersonCuriosities(hub, options = {}) {
  const about = [];
  const details = [];
  const list = usefulCuriosities(hub);
  if (!list.length) return { about, details };

  const preferredPlatform = String(options.preferredPlatform || '').toLowerCase();
  const hasCreatorChannel = options.hasCreatorChannel
    ?? (Array.isArray(hub?.streamingPlatforms) && hub.streamingPlatforms.some((row) =>
      PLATFORM_ABOUT_SOURCES.has(String(row.key || row.kind || '').toLowerCase())
    ));
  const hasPlatformAbout = list.some((item) =>
    PLATFORM_ABOUT_SOURCES.has(String(item.source || '').toLowerCase())
  );
  const dropEncyclopedia = hasCreatorChannel || hasPlatformAbout;
  const visible = dropEncyclopedia
    ? list.filter((item) => {
        const source = String(item.source || '').toLowerCase();
        return source !== 'wikipedia' && source !== 'entity';
      })
    : list;

  visible.forEach((item, i) => {
    const classified = classifyCuriosityItem(item);
    if (!classified) return;
    const id = item.id || `c-${i}`;
    if (classified.type === 'about') {
      about.push({
        id,
        body: classified.body,
        url: item.url,
        source: classified.source || String(item.source || '').toLowerCase(),
      });
    } else {
      details.push({
        id,
        label: classified.label,
        value: classified.value,
        url: item.url,
      });
    }
  });

  const aboutParagraph = pickAboutParagraph(about, preferredPlatform);
  return { about: aboutParagraph, details };
}

function normalizeAbout(text) {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

function firstAboutParagraph(text, maxSentences = 2) {
  const sentences = String(text).split(/(?<=[.!?])\s+/).filter(Boolean);
  if (sentences.length <= maxSentences) return String(text).trim();
  return sentences.slice(0, maxSentences).join(' ');
}

function pickAboutParagraph(about, preferredPlatform) {
  if (!about.length) return [];
  const unique = [];
  for (const block of about) {
    const key = normalizeAbout(block.body).slice(0, 96);
    const duplicate = unique.some((existing) => {
      const other = normalizeAbout(existing.body);
      return other.includes(key) || key.includes(other.slice(0, 96));
    });
    if (!duplicate) unique.push(block);
  }

  const platformAbout = unique.filter((block) => PLATFORM_ABOUT_SOURCES.has(block.source));
  const pool = platformAbout.length ? platformAbout : unique;
  const preferred = preferredPlatform
    ? pool.find((block) => block.source === preferredPlatform)
    : null;
  const best = preferred || [...pool].sort((a, b) => {
    const rankA = PLATFORM_ABOUT_RANK[a.source] ?? 9;
    const rankB = PLATFORM_ABOUT_RANK[b.source] ?? 9;
    if (rankA !== rankB) return rankA - rankB;
    return b.body.length - a.body.length;
  })[0];

  const body = PLATFORM_ABOUT_SOURCES.has(best.source)
    ? String(best.body).trim()
    : firstAboutParagraph(best.body);
  return [{ ...best, body }];
}
